import React, { useState, useEffect } from 'react';
import { getPlansCacheStatus } from '../services/cache-api';
import {
  getColorForStatus,
  getLoadingCacheProgressPercentage,
  getLoadingTextForStatus,
  initialCacheStatusValue,
  LoadCacheStatus,
} from '../models/plans/LoadCacheStatus';

interface CacheStatusProps {
  children: React.ReactNode;
}

export const CacheStatus: React.FC<CacheStatusProps> = ({ children }) => {
  const [cacheStatus, setCacheStatus] = useState<LoadCacheStatus>(
    initialCacheStatusValue,
  );
  const [showExitMessage, setShowExitMessage] = useState<boolean>(false);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    let exitMessageTimeoutId: NodeJS.Timeout | null = null;

    const fetchCacheStatus = async () => {
      try {
        const status: LoadCacheStatus = await getPlansCacheStatus();
        setCacheStatus(status);

        // Check exit conditions
        if (status.finished || status.errors) {
          // Stop polling
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }

          // Set exit condition
          setShowExitMessage(true);

          // Hide message after 30 seconds
          exitMessageTimeoutId = setTimeout(() => {
            setShowExitMessage(false);
          }, 30000);
        }
      } catch (error) {
        console.error('Error fetching cache status:', error);
      }
    };

    // Initial fetch
    fetchCacheStatus();

    // Set up polling every 5 seconds
    intervalId = setInterval(() => {
      fetchCacheStatus();
    }, 5000);

    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (exitMessageTimeoutId) {
        clearTimeout(exitMessageTimeoutId);
      }
    };
  }, []);

  const progressPercentage = getLoadingCacheProgressPercentage(cacheStatus);
  const progressBarClassName = getColorForStatus(cacheStatus);

  const shouldShowProgressBar = !cacheStatus.finished && !cacheStatus.errors;
  const statusText = getLoadingTextForStatus(cacheStatus);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow">{children}</div>
      {(shouldShowProgressBar || showExitMessage) && (
        <footer
          className={`w-full border-t border-base-300 ${
            showExitMessage && cacheStatus.errors
              ? 'bg-pink-300'
              : 'bg-base-200'
          }`}
        >
          <div className="container mx-auto px-4 py-3">
            {shouldShowProgressBar && (
              <div className="w-full">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-base-content">
                    {statusText}
                  </span>
                  <span className="text-sm font-medium text-base-content">
                    {progressPercentage}%
                  </span>
                </div>
                <progress
                  className={progressBarClassName}
                  value={progressPercentage}
                  max="100"
                >
                  {progressPercentage}%
                </progress>
              </div>
            )}
            {showExitMessage && (
              <div className="w-full">
                <div className="text-sm text-base-content text-center">
                  {statusText}
                </div>
              </div>
            )}
          </div>
        </footer>
      )}
    </div>
  );
};
